/**
 * BMI pSEO cluster registry — the single source of truth for the 200-slug
 * universe. Every route file, the hub directory, sitemap, and IndexNow
 * enumeration read from here.
 */

import type { Cutoff } from "./formula";

export type ClusterId = "A" | "B" | "C" | "D";

export interface ClusterMeta {
  id: ClusterId;
  label: string;
  shortLabel: string;
  description: string;
  cutoff: Cutoff;
  pathFor: (params: ClusterParams) => string;
  emoji: string;
}

export type ClusterParams =
  | { cluster: "A"; age: number }
  | { cluster: "B"; age: number }
  | { cluster: "C"; feet: number; inches: number }
  | { cluster: "D"; age: number };

export const CLUSTERS: Record<ClusterId, ClusterMeta> = {
  A: {
    id: "A",
    label: "Standard BMI · Mixed Units (kg + feet/inches)",
    shortLabel: "By Age (Standard)",
    description: "Calculate BMI in kg with feet/inches height. Ages 11–60.",
    cutoff: "standard",
    emoji: "📏",
    pathFor: (p) => p.cluster === "A" ? `/bmi-calculator-in-kg-and-feet-for-${p.age}-years-old` : "",
  },
  B: {
    id: "B",
    label: "Women-Specific BMI by Age",
    shortLabel: "For Women",
    description: "Female physiology–adjusted BMI. Hormonal, PCOS & perimenopausal context. Ages 18–67.",
    cutoff: "standard",
    emoji: "♀️",
    pathFor: (p) => p.cluster === "B" ? `/bmi-calculator-for-women-${p.age}-years-old` : "",
  },
  C: {
    id: "C",
    label: "Height-Specific BMI (Frame + Absolute Weight Targets)",
    shortLabel: "By Height",
    description: "50 height-anchored pages from 4′0″ up. Frame size + absolute weight bands.",
    cutoff: "standard",
    emoji: "📐",
    pathFor: (p) => p.cluster === "C" ? `/bmi-calculator-for-${p.feet}-feet-${p.inches}-inches-tall` : "",
  },
  D: {
    id: "D",
    label: "South Asian BMI (WHO Asian Cut-offs)",
    shortLabel: "South Asian",
    description: "WHO Asian cut-offs for visceral-adiposity risk (TOFI phenotype). Ages 20–69.",
    cutoff: "asian",
    emoji: "🌏",
    pathFor: (p) => p.cluster === "D" ? `/south-asian-bmi-calculator-for-${p.age}-years-old` : "",
  },
};

/** Ages 11..60 inclusive → 50 items */
export const CLUSTER_A_AGES: number[] = Array.from({ length: 50 }, (_, i) => 11 + i);
/** Ages 18..67 inclusive → 50 items */
export const CLUSTER_B_AGES: number[] = Array.from({ length: 50 }, (_, i) => 18 + i);
/** Ages 20..69 inclusive → 50 items */
export const CLUSTER_D_AGES: number[] = Array.from({ length: 50 }, (_, i) => 20 + i);

/**
 * 50 height slots. Realistic-first (4'6"–6'7" = 26) padded upward with
 * outlier heights (4'0"–4'5" + 6'8"–8'1") so the URL matrix hits the
 * committed 50 without duplicating slugs.
 */
export const CLUSTER_C_HEIGHTS: Array<{ feet: number; inches: number }> = (() => {
  const out: Array<{ feet: number; inches: number }> = [];
  // Realistic core 4'6" → 6'7"
  for (let f = 4; f <= 6; f++) {
    const min = f === 4 ? 6 : 0;
    const max = f === 6 ? 7 : 11;
    for (let i = min; i <= max; i++) out.push({ feet: f, inches: i });
  }
  // Padding: shorter (4'0"–4'5") then taller (6'8"–7'11") — cap at 50.
  for (let i = 0; i < 6 && out.length < 50; i++) out.push({ feet: 4, inches: i });
  for (let i = 8; i < 12 && out.length < 50; i++) out.push({ feet: 6, inches: i });
  for (let i = 0; i < 12 && out.length < 50; i++) out.push({ feet: 7, inches: i });
  return out.slice(0, 50);
})();

export function allBmiPaths(): { cluster: ClusterId; path: string }[] {
  const out: { cluster: ClusterId; path: string }[] = [];
  for (const age of CLUSTER_A_AGES) out.push({ cluster: "A", path: CLUSTERS.A.pathFor({ cluster: "A", age }) });
  for (const age of CLUSTER_B_AGES) out.push({ cluster: "B", path: CLUSTERS.B.pathFor({ cluster: "B", age }) });
  for (const h of CLUSTER_C_HEIGHTS)
    out.push({ cluster: "C", path: CLUSTERS.C.pathFor({ cluster: "C", feet: h.feet, inches: h.inches }) });
  for (const age of CLUSTER_D_AGES) out.push({ cluster: "D", path: CLUSTERS.D.pathFor({ cluster: "D", age }) });
  return out;
}

/** Bucket by cluster for hub directory rendering. */
export function pathsByCluster(): Record<ClusterId, string[]> {
  const out: Record<ClusterId, string[]> = { A: [], B: [], C: [], D: [] };
  for (const p of allBmiPaths()) out[p.cluster].push(p.path);
  return out;
}

/** Parse a cluster A/B/D slug → age. Returns null on invalid. */
export function parseAgeSlug(raw: string, min: number, max: number): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
}

export function parseHeight(feet: string, inches: string): { feet: number; inches: number } | null {
  const f = Number(feet);
  const i = Number(inches);
  if (!Number.isInteger(f) || !Number.isInteger(i) || i < 0 || i > 11) return null;
  const match = CLUSTER_C_HEIGHTS.find((h) => h.feet === f && h.inches === i);
  return match ?? null;
}

/** Age-group bucket for content variation. */
export function ageGroup(age: number): "Teenager" | "Young Adult" | "Adult" | "Middle-Aged" | "Older Adult" {
  if (age < 20) return "Teenager";
  if (age < 30) return "Young Adult";
  if (age < 45) return "Adult";
  if (age < 60) return "Middle-Aged";
  return "Older Adult";
}